import React from "react";

/**
 * PaxDecreaseWarningModal - Shows when user tries to decrease PAX but items exceed new limits
 * @param {Array} categoriesExceeded - List of categories that would exceed limits
 * @param {number} currentPax - Current PAX value
 * @param {number} targetPax - Attempted new PAX value
 * @param {Function} onClose - Callback to close the modal
 */
const PaxDecreaseWarningModal = ({ categoriesExceeded, currentPax, targetPax, onClose }) => {
  if (!categoriesExceeded || categoriesExceeded.length === 0) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
      <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-shrink-0">
            <svg className="w-8 h-8 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">Cannot Decrease PAX</h3>
            <p className="text-sm text-gray-600">
              Reducing PAX from {currentPax} to {targetPax} would exceed category limits
            </p>
          </div>
        </div>

        {/* Categories List */}
        <div className="mb-6">
          <p className="text-sm text-gray-700 mb-3 font-semibold">
            Please remove items from the following {categoriesExceeded.length === 1 ? 'category' : 'categories'}:
          </p>
          <div className="space-y-3 max-h-60 overflow-y-auto">
            {categoriesExceeded.map((category, index) => (
              <div 
                key={category.categoryId || index} 
                className="bg-amber-50 border border-amber-200 rounded-lg p-3"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900 text-sm">
                      {category.categoryName}
                    </p>
                  </div>
                  <span className="ml-2 px-2 py-0.5 bg-amber-600 text-white text-xs font-bold rounded">
                    {category.excessItems} excess
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="bg-white rounded px-2 py-1">
                    <span className="text-gray-600">Current Items:</span>
                    <span className="ml-1 font-bold text-gray-900">{category.currentItems}</span>
                  </div>
                  <div className="bg-white rounded px-2 py-1">
                    <span className="text-gray-600">New Limit:</span>
                    <span className="ml-1 font-bold text-gray-900">{category.newLimit}</span>
                  </div>
                </div>
                <p className="text-xs text-amber-700 mt-2">
                  Remove {category.excessItems} {category.excessItems === 1 ? 'item' : 'items'} to proceed
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Instructions */}
        <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-xs text-blue-800">
            <span className="font-semibold">Note:</span> Only non-addon and non-complimentary items count toward category limits. 
            Remove regular items (not marked as "half", "full", or complimentary) to decrease PAX.
          </p>
        </div>

        {/* Actions */}
        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-[#86604D] text-white rounded-lg hover:bg-[#6B4A3A] transition-colors font-semibold"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaxDecreaseWarningModal;

